import { Contact } from '~api/address_book';

import { Add16 } from '@ingrid/iconography/react';
import { Button, ButtonLink, Card, ListLayout } from '@ingrid/luterkort-react';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { useTranslation } from '~src/i18n/use-translation';

import { useShipmentHydrateMutation } from '../mutations';
import { useShipmentFormContext } from '../types';
import { useAddresses } from './addresses-context';
import { ReferencesSelectExternal, ReferencesSelectExternalProps } from './references-select-external';
import { ReferencesSelectTos, ReferencesSelectTosProps } from './references-select-tos';

export function References() {
  const { watch, setValue, reset, getValues } = useShipmentFormContext();
  const { setContacts } = useAddresses();
  const { t } = useTranslation();

  const hydrateMutation = useShipmentHydrateMutation();

  const [tos, external_id] = watch(['tos', 'external_id']);

  const tosModalProps: Omit<ReferencesSelectTosProps, 'trigger'> = {
    mode: 'add',
    onValueChange: async order => {
      if (!order) {
        return;
      }

      try {
        const shipment = await hydrateMutation.mutateAsync({
          ...getValues(),
          tos: order,
        });

        reset(shipment);

        setContacts(
          [shipment.contact_from, shipment.contact_to, shipment.customer_info?.contact].filter(Boolean) as Contact[]
        );
      } catch (e) {
        setValue('tos', order);
        throw e;
      }
    },
    value: tos,
  };

  const otherModalProps: Omit<ReferencesSelectExternalProps, 'trigger'> = {
    mode: 'add',
    value: { externalId: external_id },
    onSubmit: values => setValue('external_id', values?.externalId, { shouldDirty: true }),
  };

  return (
    <Card.Wrapper as="section">
      <Card.Header>{t('common.references')}</Card.Header>
      <Card.Content>
        {tos || external_id ? (
          <ListLayout.Column>
            {tos ? (
              <ListLayout.Row data-testid="reference.tos_id">
                <ListLayout.Stack>
                  <ListLayout.SecondaryText>{t('common.orderId')}</ListLayout.SecondaryText>
                  <ListLayout.PrimaryText>{tos.id}</ListLayout.PrimaryText>
                </ListLayout.Stack>
                <ListLayout.Actions>
                  <ReferencesSelectTos
                    {...tosModalProps}
                    mode="update"
                    trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
                  />
                  <ClearConfirmationModal
                    onSubmit={() => setValue('tos', undefined)}
                    clearedItemName={'common.orderId'}
                  />
                </ListLayout.Actions>
              </ListLayout.Row>
            ) : (
              <ListLayout.Row>
                <ReferencesSelectTos
                  {...tosModalProps}
                  trigger={
                    <ButtonLink>
                      <Add16 /> {t('shipments.references.addTosId')}
                    </ButtonLink>
                  }
                />
              </ListLayout.Row>
            )}
            {external_id ? (
              <ListLayout.Row data-testid="reference.external_id">
                <ListLayout.Stack>
                  <ListLayout.SecondaryText>{t('common.orderNumber')}</ListLayout.SecondaryText>
                  <ListLayout.PrimaryText>{external_id}</ListLayout.PrimaryText>
                </ListLayout.Stack>
                <ListLayout.Actions>
                  <ReferencesSelectExternal
                    {...otherModalProps}
                    mode="update"
                    trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
                  />
                  <ClearConfirmationModal
                    onSubmit={() => setValue('external_id', undefined)}
                    clearedItemName={'common.orderNumber'}
                  />
                </ListLayout.Actions>
              </ListLayout.Row>
            ) : (
              <ListLayout.Row>
                <ReferencesSelectExternal
                  {...otherModalProps}
                  trigger={
                    <ButtonLink>
                      <Add16 /> {t('shipments.references.addOther')}
                    </ButtonLink>
                  }
                />
              </ListLayout.Row>
            )}
          </ListLayout.Column>
        ) : (
          <ListLayout.Row>
            <ReferencesSelectTos
              {...tosModalProps}
              trigger={
                <Button variant="secondary">
                  <Add16 /> {t('shipments.references.addTosId')}
                </Button>
              }
            />
            <ReferencesSelectExternal
              {...otherModalProps}
              trigger={
                <Button variant="secondary">
                  <Add16 /> {t('shipments.references.addOther')}
                </Button>
              }
            />
          </ListLayout.Row>
        )}
      </Card.Content>
    </Card.Wrapper>
  );
}
